import React, { useState, useEffect, useRef } from 'react';
import { RegistryCompound, InventoryLocation } from '../../types/inventory';
import { inventoryAPI } from '../../services/inventoryApi';

interface Props {
  onAdded: () => void;
}

const CATEGORIES = [
  { value: 'compound', label: 'Registered Compound' },
  { value: 'reagent', label: 'Reagent' },
  { value: 'building_block', label: 'Building Block' },
  { value: 'consumable', label: 'Consumable' },
  { value: 'biological', label: 'Biological' },
  { value: 'external', label: 'External' },
];

const CONTAINER_TYPES = [
  { value: 'vial', label: 'Vial' },
  { value: 'tube', label: 'Tube' },
  { value: 'plate_well', label: 'Plate Well' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'bag', label: 'Bag' },
  { value: 'other', label: 'Other' },
];

const AddToStockForm: React.FC<Props> = ({ onAdded }) => {
  // Section A: Compound source
  const [category, setCategory] = useState('compound');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RegistryCompound[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedCompound, setSelectedCompound] = useState<RegistryCompound | null>(null);
  const [unregName, setUnregName] = useState('');
  const [unregSmiles, setUnregSmiles] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Section B: Batch details
  const [purityPercent, setPurityPercent] = useState('');
  const [saltForm, setSaltForm] = useState('');
  const [polymorph, setPolymorph] = useState('');
  const [supplier, setSupplier] = useState('');
  const [notebookRef, setNotebookRef] = useState('');
  const [synthesisDate, setSynthesisDate] = useState('');
  const [amountMade, setAmountMade] = useState('');
  const [amountUnit, setAmountUnit] = useState('mg');

  // Section C: Physical location/container
  const [containerType, setContainerType] = useState('vial');
  const [currentAmount, setCurrentAmount] = useState('');
  const [locationId, setLocationId] = useState<number | undefined>(undefined);
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);

  const isRegistered = category === 'compound';
  const missingRequirements = isRegistered
    ? (selectedCompound ? [] : ['Select a registered compound from the search results'])
    : (unregName.trim() ? [] : ['Enter an item name']);
  const canSubmit = missingRequirements.length === 0;

  // Load locations
  useEffect(() => {
    inventoryAPI.getLocationTree()
      .then(res => setLocations(flattenLocations(res.data)))
      .catch(() => {});
  }, []);

  // Debounced registry search
  useEffect(() => {
    if (!isRegistered || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearching(true);
      inventoryAPI.registrySearch(searchQuery)
        .then(res => setSearchResults(res.data))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchQuery, isRegistered]);

  // When switching to non-registered category, clear compound selection
  useEffect(() => {
    if (!isRegistered) {
      setSelectedCompound(null);
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isRegistered]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!canSubmit) {
      setError(`Required before adding to stock: ${missingRequirements.join(', ')}.`);
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        category,
        amount_unit: amountUnit,
        container_type: containerType,
      };

      if (isRegistered) {
        payload.registry_parent_id = selectedCompound!.parent_id;
      } else {
        payload.name = unregName.trim();
        if (unregSmiles.trim()) payload.smiles = unregSmiles.trim();
      }

      if (purityPercent) payload.purity_percent = parseFloat(purityPercent);
      if (saltForm.trim()) payload.salt_form = saltForm.trim();
      if (polymorph.trim()) payload.polymorph = polymorph.trim();
      if (supplier.trim()) payload.supplier = supplier.trim();
      if (notebookRef.trim()) payload.notebook_ref = notebookRef.trim();
      if (synthesisDate) payload.synthesis_date = synthesisDate;
      if (amountMade) payload.amount_made = parseFloat(amountMade);
      if (currentAmount) payload.current_amount = parseFloat(currentAmount);
      if (locationId) payload.location_id = locationId;
      if (expiryDate) payload.expiry_date = expiryDate;
      if (notes.trim()) payload.notes = notes.trim();

      const res = await inventoryAPI.addToStock(payload);
      setResult(res.data);
      onAdded();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add stock');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompound = (compound: RegistryCompound) => {
    setSelectedCompound(compound);
    setSearchQuery(compound.parent_code);
    setSearchResults([]);
    setError('');
  };

  return (
    <div style={{ padding: 24, maxWidth: 680, overflow: 'auto' }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
        Add to Stock
      </h3>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>
        Add physical material from the registry or create an unregistered item (reagent, building block, etc.)
      </p>

      <form onSubmit={handleSubmit}>
        {/* ── Section A: Compound Source ── */}
        <SectionHeader label="A" title="Select Compound" />

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            style={inputStyle}
            disabled={loading}
          >
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {isRegistered ? (
          <>
            <div style={{ marginBottom: 16, position: 'relative' }}>
              <label style={labelStyle}>Search and Select Registry Compound *</label>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Search by DIS- code, SMILES, formula or InChIKey, then choose a result.
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setSelectedCompound(null); setError(''); }}
                placeholder="e.g. DIS-000001 or CC(=O)Oc1ccccc1C(O)=O"
                style={inputStyle}
                disabled={loading}
                aria-required="true"
                aria-invalid={!selectedCompound}
              />
              {searching && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Searching registry...
                </div>
              )}
              {searchResults.length > 0 && !selectedCompound && (
                <div style={{
                  position: 'absolute', left: 0, right: 0, top: '100%',
                  zIndex: 100, maxHeight: 260, overflow: 'auto',
                  backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)',
                  borderRadius: 6, marginTop: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                }}>
                  {searchResults.map(c => (
                    <div
                      key={c.parent_id}
                      onClick={() => handleSelectCompound(c)}
                      style={{
                        padding: '10px 14px', cursor: 'pointer',
                        borderBottom: '1px solid var(--card-border)',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(13,148,136,0.08)')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#0D9488' }}>
                          {c.parent_code}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                          {c.mol_formula || ''}
                          {c.mol_weight ? ` (${c.mol_weight.toFixed(1)})` : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, fontFamily: 'monospace' }}>
                        {c.canonical_smiles.length > 60 ? c.canonical_smiles.substring(0, 60) + '...' : c.canonical_smiles}
                      </div>
                      {c.therapeutic_area_name && (
                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {c.therapeutic_area_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {searchQuery.trim() && !selectedCompound && !searching && searchResults.length === 0 && (
                <div role="status" style={{ fontSize: 12, color: '#D97706', marginTop: 6 }}>
                  A compound has not been selected yet. Choose one from the search results before adding stock.
                </div>
              )}
            </div>

            {selectedCompound && (
              <div style={{
                padding: '12px 16px', borderRadius: 8, marginBottom: 16,
                border: '1px solid rgba(13,148,136,0.3)', backgroundColor: 'rgba(13,148,136,0.05)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0D9488' }}>
                    {selectedCompound.parent_code}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedCompound(null); setSearchQuery(''); }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14 }}
                  >
                    &times; Change
                  </button>
                </div>
                <InfoLine label="SMILES" value={selectedCompound.canonical_smiles} mono />
                <InfoLine label="Formula" value={selectedCompound.mol_formula} />
                <InfoLine label="MW" value={selectedCompound.mol_weight?.toFixed(2)} />
                <InfoLine label="InChIKey" value={selectedCompound.inchi_key} mono />
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Item Name *</label>
              <input
                type="text"
                value={unregName}
                onChange={e => { setUnregName(e.target.value); setError(''); }}
                placeholder="e.g. Triethylamine, DCM, Pd/C catalyst"
                style={inputStyle}
                disabled={loading}
                required
                aria-required="true"
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>SMILES (optional)</label>
              <input
                type="text"
                value={unregSmiles}
                onChange={e => setUnregSmiles(e.target.value)}
                placeholder="e.g. CCN(CC)CC"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
                disabled={loading}
              />
            </div>
          </>
        )}

        {/* ── Section B: Batch Details ── */}
        <SectionHeader label="B" title="Batch Details" />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Purity %</label>
            <input
              type="number"
              value={purityPercent}
              onChange={e => setPurityPercent(e.target.value)}
              placeholder="e.g. 99.5"
              style={inputStyle}
              disabled={loading}
              min={0} max={100} step={0.1}
            />
          </div>
          <div>
            <label style={labelStyle}>Salt Form</label>
            <input
              type="text"
              value={saltForm}
              onChange={e => setSaltForm(e.target.value)}
              placeholder="e.g. HCl, TFA, free base"
              style={inputStyle}
              disabled={loading}
            />
          </div>
          <div>
            <label style={labelStyle}>Polymorph</label>
            <input
              type="text"
              value={polymorph}
              onChange={e => setPolymorph(e.target.value)}
              placeholder="e.g. Form A"
              style={inputStyle}
              disabled={loading}
            />
          </div>
          <div>
            <label style={labelStyle}>Supplier</label>
            <input
              type="text"
              value={supplier}
              onChange={e => setSupplier(e.target.value)}
              placeholder="e.g. Sigma-Aldrich"
              style={inputStyle}
              disabled={loading}
            />
          </div>
          <div>
            <label style={labelStyle}>Notebook Ref</label>
            <input
              type="text"
              value={notebookRef}
              onChange={e => setNotebookRef(e.target.value)}
              placeholder="e.g. ELN-2024-001"
              style={inputStyle}
              disabled={loading}
            />
          </div>
          <div>
            <label style={labelStyle}>Synthesis Date</label>
            <input
              type="date"
              value={synthesisDate}
              onChange={e => setSynthesisDate(e.target.value)}
              style={inputStyle}
              disabled={loading}
            />
          </div>
          <div>
            <label style={labelStyle}>Amount Made</label>
            <input
              type="number"
              value={amountMade}
              onChange={e => setAmountMade(e.target.value)}
              placeholder="e.g. 500"
              style={inputStyle}
              disabled={loading}
              min={0} step={0.1}
            />
          </div>
          <div>
            <label style={labelStyle}>Unit</label>
            <select value={amountUnit} onChange={e => setAmountUnit(e.target.value)} style={inputStyle} disabled={loading}>
              <option value="mg">mg</option>
              <option value="g">g</option>
              <option value="kg">kg</option>
              <option value="mL">mL</option>
              <option value="L">L</option>
            </select>
          </div>
        </div>

        {/* ── Section C: Physical Location & Container ── */}
        <SectionHeader label="C" title="Physical Location & Container" />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Container Type</label>
            <select value={containerType} onChange={e => setContainerType(e.target.value)} style={inputStyle} disabled={loading}>
              {CONTAINER_TYPES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Current Amount</label>
            <input
              type="number"
              value={currentAmount}
              onChange={e => setCurrentAmount(e.target.value)}
              placeholder="Defaults to amount made"
              style={inputStyle}
              disabled={loading}
              min={0} step={0.1}
            />
          </div>
          <div>
            <label style={labelStyle}>Storage Location</label>
            <select
              value={locationId || ''}
              onChange={e => setLocationId(e.target.value ? parseInt(e.target.value) : undefined)}
              style={inputStyle}
              disabled={loading}
            >
              <option value="">-- No location --</option>
              {locations.map(loc => (
                <option key={loc.location_id} value={loc.location_id}>
                  {loc.materialised_path || loc.name}
                  {loc.temperature_zone ? ` (${loc.temperature_zone})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Expiry Date</label>
            <input
              type="date"
              value={expiryDate}
              onChange={e => setExpiryDate(e.target.value)}
              style={inputStyle}
              disabled={loading}
            />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Any additional notes about this stock..."
            style={{ ...inputStyle, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
            disabled={loading}
          />
        </div>

        {/* Required-field guidance and submit */}
        {!canSubmit && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginBottom: 12, padding: '11px 14px', borderRadius: 6,
              backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)',
              color: '#B45309', fontSize: 12,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Required before adding to stock</div>
            {missingRequirements.map(requirement => (
              <div key={requirement}>&bull; {requirement}</div>
            ))}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !canSubmit}
          aria-disabled={loading || !canSubmit}
          title={!canSubmit ? missingRequirements.join('. ') : 'Add this item to stock'}
          style={{
            padding: '10px 28px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: loading || !canSubmit ? '#9CA3AF' : '#0D9488',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: loading ? 'wait' : !canSubmit ? 'not-allowed' : 'pointer',
            opacity: !canSubmit ? 0.72 : 1,
            transition: 'background-color 0.15s, opacity 0.15s',
          }}
        >
          {loading ? 'Adding to Stock...' : 'Add to Stock'}
        </button>
      </form>

      {/* Error */}
      {error && (
        <div style={{
          marginTop: 16, padding: '12px 16px', borderRadius: 6,
          backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
          color: '#EF4444', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* Success */}
      {result && (
        <div style={{
          marginTop: 16, padding: '16px 20px', borderRadius: 8,
          backgroundColor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#10B981', marginBottom: 8 }}>
            Stock Added Successfully
          </div>
          {result.chemistry_code && <InfoLine label="Inventory Code" value={result.chemistry_code} highlight />}
          {result.batch_code && <InfoLine label="Batch" value={result.batch_code} />}
          {result.barcode && <InfoLine label="Barcode" value={result.barcode} mono />}
          {result.registry_code && <InfoLine label="Registry Code" value={result.registry_code} highlight />}
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
            Click on the compound in the grid to see patent collisions, bioactivity, and pathway data.
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helper components ──

const SectionHeader: React.FC<{ label: string; title: string }> = ({ label, title }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 14, marginTop: 8,
    paddingBottom: 8, borderBottom: '1px solid var(--card-border)',
  }}>
    <span style={{
      width: 22, height: 22, borderRadius: '50%',
      backgroundColor: '#0D9488', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700, flexShrink: 0,
    }}>
      {label}
    </span>
    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
      {title}
    </span>
  </div>
);

const InfoLine: React.FC<{ label: string; value?: string | null; mono?: boolean; highlight?: boolean }> = ({
  label, value, mono, highlight
}) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span style={{
      color: highlight ? '#0D9488' : 'var(--text-primary)',
      fontFamily: mono ? 'monospace' : 'inherit',
      fontWeight: highlight ? 600 : 400,
      fontSize: mono ? 11 : 12,
      maxWidth: 360,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}>
      {value || '-'}
    </span>
  </div>
);

function flattenLocations(tree: InventoryLocation[]): InventoryLocation[] {
  const flat: InventoryLocation[] = [];
  const walk = (nodes: InventoryLocation[]) => {
    for (const n of nodes) {
      flat.push(n);
      if (n.children) walk(n.children);
    }
  };
  walk(tree);
  return flat;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--card-border)',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text-primary)',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box' as any,
};

export default AddToStockForm;
